import React from "react";
import { UserFriendlyCertificateStatus } from "common/constants";
import { buildLink } from "common/routing";
import {
  documentUploadDisabled,
  formatDocumentUploadName,
} from "common-client/utils/documentUploads";
import {
  AccountDocumentType,
  CertificateUploadStatus,
  DocumentUpload,
  Property,
} from "../../../generated/graphql";
import { StyledLink } from "../../ActivityFeed/__styles__/Message";
import { DynamicOverflowWrapper } from "../../Common/DynamicOverflowWrapper";
import { Body } from "../../Common/Typography";
import ActionButton from "../../DocumentUploads/ActionButton";
import {
  Bullet,
  Row,
  Subtitle,
  TextContainer,
  TitleContainer,
  ViewRecordContainer,
} from "./__styles__/ListItem";
import DocumentUploadIcon from "./DocumentUploadIcon";
import { ViewDocumentLink } from "./ViewDocumentLink";

export interface ListItemProps {
  elevationCertificateId?: string;
  formData?: Record<string, string>;
  pathname?: string;
  search?: string;
  status?: Maybe<CertificateUploadStatus>;
  submission?: Maybe<{
    id: string;
    submissionTypeVersion: { submissionType: { name: string } };
  }>;
  objectId?: Maybe<string>;
  onUpdate?: () => void;
  documentUpload: Pick<
    DocumentUpload,
    | "id"
    | "createdAt"
    | "issuedAt"
    | "originalFilename"
    | "hiddenFromPublic"
    | "isDeletable"
    | "mimeType"
  > & {
    accountDocumentType: Pick<AccountDocumentType, "name" | "id">;
    property?: Maybe<
      Pick<Property, "id" | "streetAddress" | "longitude" | "latitude">
    >;
  };
}

export default ({
  elevationCertificateId,
  formData,
  pathname,
  search,
  status,
  submission,
  onUpdate,
  documentUpload,
}: ListItemProps) => {
  const prevLocation = `${pathname}${search}`;

  const cancelled = status === CertificateUploadStatus.CANCELED;
  const disabled = documentUploadDisabled({
    cancelled,
    status: status || null,
  });

  const linkProps = {
    documentUploadId: documentUpload.id,
    elevationCertificateId,
    accountDocumentType: documentUpload.accountDocumentType,
    prevLocation,
    disabled,
  };

  const { issuedAt, createdAt, accountDocumentType, originalFilename } =
    documentUpload;

  const documentUploadTitle = formatDocumentUploadName({
    isSubmission: !!formData,
    createdAt,
    issuedAt,
    accountDocumentTypeName: accountDocumentType.name,
  });

  let fileNameText = originalFilename;
  if (disabled || cancelled) {
    fileNameText += ` - ${UserFriendlyCertificateStatus[status!]}`;
  }

  return (
    <Row>
      <TitleContainer>
        <DocumentUploadIcon documentUploadMimeType={documentUpload.mimeType} />
        <TextContainer>
          <Body
            size="default"
            type="emphasis"
            color={disabled ? "contentDisabled" : "contentPrimary"}
          >
            {documentUploadTitle}
          </Body>
          <Subtitle>
            <DynamicOverflowWrapper
              Link={ViewDocumentLink}
              linkProps={linkProps}
              text={fileNameText}
            />

            {/* Because we are using flex, I need the bullet and the submission link to be siblings with the document name */}
            {!!submission && <Bullet>&#9679;</Bullet>}
            {!!submission && (
              <ViewRecordContainer>
                <StyledLink
                  to={{
                    pathname: buildLink("viewSubmission", {
                      submissionId: submission.id,
                    }),
                    state: { prevLocation },
                  }}
                  data-testid="view-submission-link"
                >
                  View related record
                </StyledLink>
              </ViewRecordContainer>
            )}
          </Subtitle>
        </TextContainer>
      </TitleContainer>
      <ActionButton
        documentUpload={documentUpload}
        disabled={disabled}
        onUpdate={onUpdate}
      />
    </Row>
  );
};
